import type {
  ApiCacheTag,
  ApiInvalidationTarget,
  ApiResourceDescriptor,
  ApiRouteInvalidationTarget,
} from "../resources/types";
import type { ClientType } from "../routing/clientType";
import { generateRoute } from "../routing/resolveRoute";
import { isApiGetKey } from "./apiKey";

export function matchesBatchApiGetCache(
  key: unknown,
  matchesEntry: (entry: unknown) => boolean,
) {
  if (!isApiGetKey(key)) return false;
  if (key.cacheResource == null) return false;
  if (!Array.isArray(key.route) || !Array.isArray(key.requestedRoute))
    return false;
  const routes = key.route;
  // Reuse scalar matchers with the shared metadata; these are not cache entries.
  return key.requestedRoute.some((requestedRoute, index) =>
    matchesEntry({ ...key, route: routes[index], requestedRoute }),
  );
}

export function matchesExactApiGetRoute(
  key: unknown,
  requestedRoute: string,
): boolean;
export function matchesExactApiGetRoute(
  key: unknown,
  route: string,
  clientType: ClientType,
): boolean;
export function matchesExactApiGetRoute(
  key: unknown,
  route: string,
  clientType?: ClientType,
) {
  if (!isApiGetKey(key)) return false;
  if (key.namespace !== "get") return false;

  if (clientType == null) return key.requestedRoute === route;
  if (key.clientType !== clientType) return false;
  if (typeof key.requestedRoute !== "string") return false;

  const requestedRoute = generateRoute(route, clientType);
  return !!requestedRoute && key.requestedRoute === requestedRoute;
}

function matchesRequestedRouteFamily(route: string, collectionRoute: string) {
  return route === collectionRoute || route.startsWith(`${collectionRoute}?`);
}

export function matchesResourceCollectionCache(
  key: unknown,
  options: {
    descriptor: ApiResourceDescriptor;
    collectionRoute: string;
    requestedCollectionRoute: string;
    namespace: "get" | "pagedGet" | "serverPagedGet";
    clientType: ClientType;
  },
) {
  const {
    descriptor,
    collectionRoute,
    requestedCollectionRoute,
    namespace,
    clientType,
  } = options;
  if (!isApiGetKey(key) || key.namespace !== namespace) return false;
  if (key.clientType !== clientType) return false;
  if (key.cacheResource?.tag !== descriptor.cacheTag) return false;
  if (key.cacheResource.collectionRoute !== collectionRoute) return false;
  if (typeof key.route !== "string") return false;
  if (typeof key.requestedRoute !== "string") return false;

  return (
    !!requestedCollectionRoute &&
    matchesRequestedRouteFamily(key.requestedRoute, requestedCollectionRoute)
  );
}

export function matchesInvalidatedResourceCache(
  key: unknown,
  invalidations: readonly ApiInvalidationTarget[],
  defaultClientType: ClientType,
): boolean {
  if (!isApiGetKey(key)) return false;
  const cacheResource = key.cacheResource;
  if (cacheResource == null) return false;

  return invalidations.some((invalidation) => {
    if (isRouteInvalidation(invalidation)) {
      if (cacheResource.tag !== invalidation.descriptor.cacheTag) {
        return false;
      }
      if (key.clientType !== invalidation.descriptor.clientType) return false;
      if (typeof key.route === "string") {
        return key.route === invalidation.route;
      }
      return key.route.includes(invalidation.route);
    }

    return matchesResourceInvalidation(
      cacheResource.tag,
      key.clientType,
      [invalidation],
      defaultClientType,
    );
  });
}

function isRouteInvalidation(
  invalidation: ApiInvalidationTarget,
): invalidation is ApiRouteInvalidationTarget {
  return typeof invalidation !== "string" && "route" in invalidation;
}

export function matchesResourceInvalidation(
  cacheTag: ApiCacheTag,
  clientType: ClientType,
  invalidations: readonly ApiInvalidationTarget[],
  defaultClientType: ClientType,
) {
  return invalidations.some((invalidation) => {
    if (typeof invalidation === "string") {
      return cacheTag === invalidation && clientType === defaultClientType;
    }
    if (isRouteInvalidation(invalidation)) return false;

    return (
      cacheTag === invalidation.cacheTag &&
      clientType === invalidation.clientType
    );
  });
}
